import React, { useEffect, useState, type JSX } from "react";
import { FaYoutube, FaLinkedin, FaInstagram } from "react-icons/fa";
import { getTenantOnlinePresence } from "../../../../../shared/services/apis";

export interface CitationData {
  citations: string[];
  search_results: SearchResult[];
  content: CompanyYouTubeInfo[];
}

export interface SearchResult {
  title: string;
  url: string;
  date: string | null;
  last_updated: string | null;
}

export interface CompanyYouTubeInfo {
  company: string;
  website: string;
  youtube_handle: string;
  youtube_channel_url: string;
  sources: string[];
}

const iconMap: Record<string, JSX.Element> = {
  youtube: <FaYoutube className="text-red-600 text-xl" />,
  linkedin: <FaLinkedin className="text-blue-600 text-xl" />,
  instagram: <FaInstagram className="text-pink-500 text-xl" />,
};

interface OnlinePresenceProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

const OnlinePresence: React.FC<OnlinePresenceProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<CitationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response = await getTenantOnlinePresence(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
         setData(response);
      } catch (error) {
        console.error("Error fetching tenant overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);
  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url); // open iframe on right side
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto mt-0 2xl:mt-20 px-4 max-h-[65vh] overflow-y-auto">
        <div className="rounded-2xl bg-white shadow-lg p-6 space-y-10 animate-pulse">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 bg-gray-200 rounded"></div> {/* icon */}
              <div className="h-5 w-24 bg-gray-200 rounded"></div> {/* title */}
            </div>
            <div className="h-4 w-20 bg-gray-200 rounded"></div> {/* link */}
          </div>

          {/* Media Cards Skeleton */}
          <div className="flex gap-4 overflow-x-auto pb-2">
            {[...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className="w-[220px] bg-white rounded-xl shadow-sm border border-gray-100 p-2 flex-shrink-0"
              >
                {/* Thumbnail */}
                <div className="rounded-lg w-full h-28 bg-gray-200"></div>

                {/* Title */}
                <div className="h-4 w-full bg-gray-200 rounded mt-2"></div>
                <div className="h-4 w-3/4 bg-gray-200 rounded mt-1"></div>

                {/* Date */}
                <div className="h-3 w-1/2 bg-gray-200 rounded mt-2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.content.length === 0) {
    return <div className="text-red-500">No data available.</div>;
  }

  // Example only for YouTube
  const youtubeResults = data.search_results.filter((result) =>
    result.url.includes("youtube.com")
  );

  return (
    <div className="max-w-4xl mx-auto mt-0 2xl:mt-20 px-4">
      <div className="rounded-2xl bg-white shadow-lg p-6 space-y-10">
        <div>
          {/* Section Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {iconMap["youtube"]}
              <h2 className="text-lg font-semibold capitalize text-gray-800">
                YouTube
              </h2>
            </div>
            <a
              href={data.content[0].youtube_channel_url}
              className="text-blue-600 text-sm hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit Channel ↗
            </a>
          </div>

          {/* Media Cards */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4  pb-2">
            {youtubeResults.map((item, idx) => (
              <div
                key={idx}
                className="w-[200px] bg-white rounded-xl  p-2 flex-shrink-0 hover:shadow-md transition"
              >
                <button
                  onClick={(e) => handleSourceClick(e, item.url)}
                  className="block"
                >
                  <img
                    src={`https://img.youtube.com/vi/${extractYouTubeId(
                      item.url
                    )}/0.jpg`}
                    alt={item.title}
                    className="rounded-lg w-full h-28 object-cover"
                  />
                </button>

                <h3 className="font-medium text-sm mt-2 line-clamp-2 text-gray-800">
                  {item.title}
                </h3>
                {item.date && (
                  <p className="text-xs text-gray-500 mt-1">
                    Published on {new Date(item.date).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      {iframeUrl && (
        <div className="fixed bottom-4 right-4 w-[30vw] h-[35vh] bg-black rounded-md shadow-xl z-50">
          <iframe
            src={iframeUrl.replace("watch?v=", "embed/")}
            className="w-full h-full rounded-xl"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <button
            onClick={() => setIframeUrl(null)}
            className="absolute top-2 right-2 bg-white text-black rounded-full px-3 py-1 text-sm shadow hover:bg-gray-200 cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

// Helper to extract YouTube video ID
function extractYouTubeId(url: string): string {
  const match = url.match(/(?:v=|\/)([0-9A-Za-z_-]{11})(?:\?|&|$)/);
  return match ? match[1] : "";
}

export default OnlinePresence;
